/**
 * ProductsPage — VEN-02 Mantenedor de Productos/Servicios
 */
import { ordenControlado } from '@/components/ds/ordenControlado'
import { sortStateToParam, type SortState } from '@/components/SortableHeader'
import { useState, useEffect, useMemo } from 'react'
import {
  Plus,
  PencilLine,
  PowerOff,
  RefreshCw,
  Eye,
  Package,
  Wrench,
  Loader2,
  ShoppingBag,
  Tag,
  Upload,
  BookOpen,
  History,
  Trash2,
} from 'lucide-react'
import { FilterBar, useFilters, type FilterConfig } from '@/components/filters'
import { EmptyState } from '@/components/EmptyState'
import { TableSkeleton } from '@/components/Skeleton'
import { DataTable, type DataTableColumn } from '@/components/ds/DataTable'
import { KebabMenu } from '@/components/KebabMenu'
import { ConfirmModal } from '@/components/ConfirmModal'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { useLocalizationConfig, useTenantTax } from '@/modules/config/hooks/useConfig'
import { CODIGOS_DETRACCION, paisUsaDetraccion } from '@/lib/documentNumbering'
import { ShoppingCart } from 'lucide-react'
import { useCartStore } from '@/store/cartStore'
import { extractFieldErrors } from '@/lib/errorUtils'
import { inputCls, labelCls, errorCls } from '@/lib/formStyles'
import {
  useProducts, useCreateProduct, useUpdateProduct,
  useDeactivateProduct, useRestoreProduct, useDriverInfo,
  useImportProductsCSV, useProductCommissionHistory, useProduct,
  type ProductListItem, type Product, type ProductPrice, type PriceCurrency,
} from '@/modules/sales/hooks/useSales'
import {
  useProductCategories, useProductAttributes,
} from '@/modules/sales/hooks/useCatalog'
import { ProductCatalogTab } from './catalog/ProductCatalogTab'
import { useAccounts, useCostCenters } from '@/modules/accounting/hooks/useAccounting'
import { Autocomplete } from '@/components/Autocomplete'
import { PageHeader } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import { NumericInput } from '@/components/NumericInput'
import { useTenantLocale, getDecimalSeparator, formatNumberForTenant, useTenantCurrency, useForeignCurrencies, currencyDecimals } from '@/lib/locale'
import { ImportCSVMapperModal, type SystemField } from '@/components/ImportCSVMapperModal'
import { ToastContainer, useToast } from '@/components/Toast'
import { useAuthStore } from '@/store/authStore'
import { usePermission } from '@/hooks/usePermission'
import { Pagination } from '@/components/ds/Pagination'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { Modal } from '@/components/ds/Modal'

// Campos base del CSV mapper (sin impuestos adicionales — esos se agregan dinámicamente)
const PRODUCT_FIELDS_BASE: SystemField[] = [
  { key: 'sku', label: 'SKU / Código', aliases: ['sku', 'codigo', 'code'] },
  { key: 'name', label: 'Nombre', required: true, aliases: ['nombre', 'name', 'item_name'] },
  { key: 'description', label: 'Descripción', aliases: ['descripcion', 'description'] },
  { key: 'item_type', label: 'Tipo (producto/servicio)', aliases: ['tipo', 'item_type', 'type', 'product_type'] },
  { key: 'unit', label: 'Unidad', aliases: ['unidad', 'unit', 'usage_unit'] },
  { key: 'unit_price', label: 'Precio Neto', required: true, aliases: ['precio', 'unit_price', 'precio_neto', 'price', 'rate'] },
  { key: 'price_currency', label: 'Moneda', aliases: ['moneda', 'price_currency', 'currency'] },
  // Neutro a propósito: esta lista se evalúa al importar el módulo, cuando
  // todavía no hay tenant del que sacar el nombre del impuesto.
  { key: 'is_tax_exempt', label: 'Exento de impuesto', aliases: ['exento', 'is_tax_exempt', 'exento_iva', 'exento_igv'] },
  { key: 'sales_account_code', label: 'Cód. Cuenta Ventas', aliases: ['account_code', 'sales_account_code', 'cuenta_ventas'] },
  { key: 'purchase_account_code', label: 'Cód. Cuenta Compras', aliases: ['purchase_account_code', 'cuenta_compras'] },
]

// ─── Helpers ──────────────────────────────────────────────────────────────────

function StatusBadge({ active }: { active: boolean }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
      ${active ? 'bg-success-surface text-success-content' : 'bg-surface-muted text-content-muted'}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-success-solid' : 'bg-content-subtle'}`} />
      {active ? 'Activo' : 'Inactivo'}
    </span>
  )
}

function TypeBadge({ type }: { type: string }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
      ${type === 'product' ? 'bg-primary-50 text-primary-hover' : 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'}`}>
      {type === 'product' ? <Package size={11} /> : <Wrench size={11} />}
      {type === 'product' ? 'Producto' : 'Servicio'}
    </span>
  )
}

// ─── Modal Producto ───────────────────────────────────────────────────────────

/** Monedas ofrecidas en los precios: la base del tenant + sus extranjeras.
 *  Reemplaza una lista fija que asumia Chile (sin PEN, y con la UF, que es
 *  chilena y no existe en el resto de los paises). */
function usePriceCurrencies(): { value: PriceCurrency; label: string }[] {
  const base = useTenantCurrency()
  const foreign = useForeignCurrencies()
  return [
    { value: base as PriceCurrency, label: base },
    ...foreign.map(([value, label]) => ({ value: value as PriceCurrency, label })),
  ]
}

function ProductModal({ open, onClose, onSubmit, isPending, initial, serverErrors, isAdmin, onShowHistory, inventoryEnabled, commissionsEnabled, readOnly }: {
  open: boolean; onClose: () => void
  onSubmit: (data: Partial<Product>) => void
  isPending: boolean; initial?: Partial<Product> | null
  serverErrors?: Record<string, string>
  isAdmin: boolean
  onShowHistory?: () => void
  inventoryEnabled?: boolean
  commissionsEnabled?: boolean
  readOnly?: boolean
}) {
  const tax = useTenantTax()
  const { register, handleSubmit, reset, setError, control, watch, setValue, formState: { errors } } = useForm<Partial<Product>>()
  const { fields: priceFields, append: appendPrice, remove: removePrice } = useFieldArray({ control, name: 'prices' as never })
  const baseCurrency = useTenantCurrency()
  const foreignCurrencies = useForeignCurrencies()
  const priceCurrencies = usePriceCurrencies()
  const defaultCurrency = (watch('price_currency') as string) || baseCurrency
  const locale = useTenantLocale()
  const decSep = getDecimalSeparator(locale)
  const [salesAccountId, setSalesAccountId] = useState('')
  const [purchaseAccountId, setPurchaseAccountId] = useState('')
  const [defaultCostCenterId, setDefaultCostCenterId] = useState('')
  // Catálogo: categoría + valores de atributos dinámicos ({code: valor})
  const [categoryId, setCategoryId] = useState('')
  const [attrValues, setAttrValues] = useState<Record<string, string>>({})
  const { data: catData } = useProductCategories({ active: 'true' })
  const categories = catData?.categories ?? []
  const { data: attrData } = useProductAttributes(categoryId ? { category: categoryId, active: 'true' } : undefined)
  const attributes = (categoryId ? attrData?.attributes : undefined) ?? []
  // Al editar, traer el detalle para sembrar categoría + atributos (la lista no los trae todos).
  const { data: productDetail } = useProduct(initial?.id ?? '')

  useEffect(() => {
    if (serverErrors) {
      Object.entries(serverErrors).forEach(([field, message]) => {
        setError(field as keyof Product, { type: 'server', message })
      })
    }
  }, [serverErrors, setError])
  const { data: driverInfo } = useDriverInfo()
  // El SPOT es exclusivo de Perú: en los demás países el campo ni se muestra.
  const { data: locConfig } = useLocalizationConfig()
  const usaDetraccion = paisUsaDetraccion(locConfig?.country_code)
  const { data: accountsData } = useAccounts({ is_imputable: 'true', is_active: 'true' })

  const accountOptions = [
    { id: '', label: '— Sin cuenta específica —' },
    ...(accountsData?.accounts.map(a => ({ id: a.id, label: `${a.code} · ${a.name}` })) ?? []),
  ]

  const { data: costCentersData } = useCostCenters({ is_active: 'true' })
  const costCenterOptions = [
    { id: '', label: '— Heredar de la categoría o del cliente —' },
    ...(costCentersData?.cost_centers.map(c => ({ id: c.id, label: `${c.code} · ${c.name}` })) ?? []),
  ]

  // Cargar datos del producto al abrir el modal
  useEffect(() => {
    if (open) {
      reset({
        is_commissionable: false,
        detraction_code: '',
        commission_rate: '0',
        commission_base: 'total',
        margin_rate: '0',
        reorder_point: '0',
        ...(initial ?? { is_tax_exempt: false, item_type: 'service', unit: 'UN', price_currency: baseCurrency as PriceCurrency }),
      })
      setSalesAccountId(initial?.sales_account?.id ?? '')
      setPurchaseAccountId(initial?.purchase_account?.id ?? '')
      setDefaultCostCenterId((initial as Partial<Product>)?.default_cost_center ?? '')
      setCategoryId((initial as Partial<Product>)?.category ?? '')
      setAttrValues({})
    }
  }, [open, initial, reset])

  // Sembrar categoría + atributos desde el detalle (solo trae attributes el detail serializer).
  useEffect(() => {
    if (open && productDetail) {
      setCategoryId(productDetail.category ?? '')
      setAttrValues(productDetail.attributes ?? {})
    }
  }, [open, productDetail])

  const wrappedSubmit = (formData: Partial<Product>) => {
    // Solo enviar atributos definidos en la categoría actual y con valor.
    const validCodes = new Set(attributes.map(a => a.code))
    const cleanedAttrs: Record<string, string> = {}
    Object.entries(attrValues).forEach(([code, val]) => {
      if (validCodes.has(code) && val !== '' && val != null) cleanedAttrs[code] = val
    })
    // Solo tocar `attributes` cuando tenemos la fuente confiable: producto nuevo
    // (sin id) o el detalle ya cargado. Evita borrar atributos si se envía el
    // form antes de que llegue el detalle al editar.
    const attrsSeeded = !initial?.id || !!productDetail
    onSubmit({
      ...formData,
      sales_account_id: salesAccountId || null,
      purchase_account_id: purchaseAccountId || null,
      default_cost_center: defaultCostCenterId || null,
      category: categoryId || null,
      ...(attrsSeeded ? { attributes: cleanedAttrs } : {}),
    } as any)
  }

  // Cerrar con Escape
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  if (!open) return null

  const UNITS = [
    { value: 'UN', label: 'Unidad (UN)' }, { value: 'KG', label: 'Kilogramo (KG)' },
    { value: 'MT', label: 'Metro (MT)' }, { value: 'LT', label: 'Litro (LT)' },
    { value: 'HR', label: 'Hora (HR)' }, { value: 'DY', label: 'Día (DY)' },
    { value: 'MO', label: 'Mes (MO)' }, { value: 'BX', label: 'Caja (BX)' },
    { value: 'OT', label: 'Otro (OT)' },
  ]

  return (
    <Modal open onClose={onClose} title={readOnly ? 'Detalle del producto/servicio' : initial?.id ? 'Editar producto/servicio' : 'Nuevo producto/servicio'} size="2xl">
      <form onSubmit={readOnly ? e => e.preventDefault() : handleSubmit(wrappedSubmit)} className="space-y-3">
        <fieldset disabled={readOnly} className="contents">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField
            dense
            label="SKU / Código interno (opcional)"
            placeholder="Auto-generado si se omite"
            inputProps={register('sku')}
            error={errors.sku?.message}
          />
          <SelectField
            dense
            label="Tipo"
            selectProps={{ ...register('item_type'), defaultValue: initial?.item_type || 'service' }}
          >
            <option value="product">Producto</option>
            <option value="service">Servicio</option>
          </SelectField>
          <TextField
            dense
            className="sm:col-span-2"
            label="Nombre"
            required
            placeholder="Nombre descriptivo"
            inputProps={register('name', { required: 'El nombre es obligatorio.' })}
            error={errors.name?.message}
          />
          <TextField
            dense
            className="sm:col-span-2"
            label="Descripción"
            multiline
            rows={2}
            placeholder="Descripción opcional…"
            inputProps={register('description')}
          />
          <SelectField
            dense
            label="Unidad de medida"
            selectProps={{ ...register('unit'), defaultValue: initial?.unit || 'UN' }}
          >
            {UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
          </SelectField>
          <div>
            <label htmlFor="producto-moneda-precio" className={labelCls}>Moneda del precio</label>
            {/* Las opciones salen del pais del tenant, no de una lista fija.
                La lista escrita a mano no tenia PEN —asi que un producto
                peruano no podia marcarse en soles y quedaba etiquetado
                «CLP»— y en cambio ofrecia la UF, que es chilena. */}
            <select id="producto-moneda-precio" {...register('price_currency')} defaultValue={initial?.price_currency || baseCurrency} className={inputCls}>
              <option value={baseCurrency}>{baseCurrency}</option>
              {foreignCurrencies.map(([code, label]) => (
                <option key={code} value={code}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="productspage-unit-price" className={labelCls}>Precio neto unitario <span className="text-danger-solid">*</span></label>
            <Controller
              name="unit_price"
              control={control}
              rules={{ required: 'El precio es obligatorio.' }}
              render={({ field }) => (
                <NumericInput id="productspage-unit-price"
                  {...field}
                  locale={locale}
                  decimals={2}
                  placeholder="0"
                  className={inputCls}
                />
              )}
            />
            <p className="mt-1 text-[11px] text-content-subtle">
              Separador decimal: <span className="font-semibold text-content-muted">"{decSep}"</span>
              {' '}— Ej: 1{decSep}50 para precio con centavos
            </p>
            {errors.unit_price && <p className={errorCls}>{errors.unit_price.message}</p>}
          </div>
        </div>

        {/* Otros precios por moneda */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs font-semibold text-content-subtle uppercase tracking-wider">Otros precios por moneda</p>
            {!readOnly && (
              <button
                type="button"
                onClick={() => {
                  const used = new Set([defaultCurrency, ...priceFields.map((_, i) => watch(`prices.${i}.currency` as any))])
                  const next = priceCurrencies.find(c => !used.has(c.value))
                  if (next) appendPrice({ currency: next.value, unit_price: '0' } as never)
                }}
                className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary-hover"
              >
                <Plus size={13} /> Agregar precio
              </button>
            )}
          </div>
          <p className="text-[11px] text-content-subtle mb-2">
            El precio principal de arriba (<span className="font-semibold">{defaultCurrency}</span>) es el predeterminado.
            Aquí se agrega el precio del mismo producto en otras monedas; al facturar se elige cuál usar.
          </p>
          {priceFields.length === 0 ? (
            <p className="text-[11px] text-content-subtle italic">Sin precios adicionales.</p>
          ) : (
            <div className="space-y-2">
              {priceFields.map((f, idx) => (
                <div key={f.id} className="flex items-center gap-2">
                  <select aria-label={`Moneda — precio adicional ${idx + 1}`}
                    {...register(`prices.${idx}.currency` as any)}
                    className={inputCls + ' sm:max-w-[200px]'}
                  >
                    {priceCurrencies.filter(c => c.value !== defaultCurrency).map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                  <Controller
                    name={`prices.${idx}.unit_price` as any}
                    control={control}
                    render={({ field }) => (
                      <NumericInput aria-label={`Precio — precio adicional ${idx + 1}`} {...field} locale={locale} decimals={2} placeholder="0" className={inputCls} />
                    )}
                  />
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => removePrice(idx)}
                      className="p-2 text-content-subtle hover:text-danger-solid transition-colors shrink-0"
                      title="Quitar"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          {errors.prices && <p className={errorCls}>{(errors.prices as any)?.message}</p>}
        </div>

        {/* Impuestos */}
        <div>
          <p className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-3">Impuestos</p>
          <div className="space-y-3 p-4 bg-glass-3 rounded-xl border border-divider">
            {/* Exención del impuesto — campo fijo del driver */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input {...register('is_tax_exempt')} type="checkbox"
                className="w-4 h-4 rounded text-primary border-divider-strong focus:ring-primary" />
              <div>
                <p className="text-sm font-medium text-content">Exento de {tax.name}</p>
                <p className="text-xs text-content-subtle">Ej: servicios de salud, educación</p>
              </div>
            </label>

            {/* Impuestos adicionales configurados por el tenant (toggles booleanos) */}
            {driverInfo?.tax_fields
              ?.filter(f => f.type === 'boolean' && f.key !== 'is_tax_exempt')
              .map(field => {
                const rawVal = watch(`tax_data.${field.key}` as any)
                const isChecked = Number(rawVal) > 0
                return (
                  <label key={field.key} className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={e =>
                        setValue(
                          `tax_data.${field.key}` as any,
                          e.target.checked ? (field.rate ?? 0) : 0,
                          { shouldDirty: true },
                        )
                      }
                      className="w-4 h-4 rounded text-primary border-divider-strong focus:ring-primary"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-content">{field.label}</p>
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold bg-warning-surface-strong text-warning-content">
                          {field.rate}%
                        </span>
                      </div>
                      {field.help_text && (
                        <p className="text-xs text-content-subtle truncate">{field.help_text}</p>
                      )}
                    </div>
                  </label>
                )
              })
            }
          </div>
        </div>

        {/* Comisión vendedor */}
        {commissionsEnabled !== false && isAdmin ? (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-content-subtle uppercase tracking-wider">Comisión vendedor</p>
              {initial?.id && onShowHistory && (
                <button type="button" onClick={onShowHistory}
                  className="inline-flex items-center gap-1 text-xs text-content-muted hover:text-primary transition-colors">
                  <History size={12} />Historial
                </button>
              )}
            </div>
            {usaDetraccion && (
              <div className="p-4 bg-glass-3 rounded-xl border border-divider space-y-3">
                {/* Solo para tenants de Perú: el SPOT no existe en los demás
                    países, y mostrar el campo sería ofrecer un régimen que su
                    normativa no contempla. */}
                <div>
                  <label htmlFor="productspage-detraction-code" className={labelCls}>Detracción (SPOT)</label>
                  <select id="productspage-detraction-code" {...register('detraction_code')} className={`${inputCls} max-w-[420px]`}>
                    <option value="">No sujeto a detracción</option>
                    {CODIGOS_DETRACCION.map((c) => (
                      <option key={c.codigo} value={c.codigo}>
                        {c.codigo} — {c.nombre} ({c.tasa}%)
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-content-subtle">
                    {watch('detraction_code')
                      ? 'El cliente retiene este porcentaje y lo deposita en la cuenta del Banco de la Nación: transfiere el resto. No aplica en operaciones de S/ 700 o menos.'
                      : 'Si este producto está sujeto al sistema de detracciones, elegí el código que le corresponde.'}
                  </p>
                </div>
              </div>
            )}
            <div className="p-4 bg-glass-3 rounded-xl border border-divider space-y-3">
              {/* El flag decide SI comisiona; sin él, la tasa no se usa nunca.
                  Por eso los campos de tasa se deshabilitan al apagarlo: evita
                  el "puse 9% y no comisiona". */}
              <label className="flex items-start gap-2 text-sm text-content">
                <input type="checkbox" {...register('is_commissionable')} className="mt-0.5" />
                <span>
                  Afecto a comisión
                  <span className="mt-0.5 block text-xs text-content-subtle">
                    Si está desactivado, este producto no paga comisión aunque el vendedor
                    tenga una tasa por defecto en su ficha.
                  </span>
                </span>
              </label>
              {watch('is_commissionable') && (
                <>
                  <div>
                    <label htmlFor="productspage-commission-base" className={labelCls}>Base de comisión</label>
                    <select id="productspage-commission-base" {...register('commission_base')} className={`${inputCls} max-w-[260px]`}>
                      <option value="total">Sobre total neto</option>
                      <option value="margin">Sobre margen</option>
                    </select>
                    <p className="mt-1 text-xs text-content-subtle">
                      {watch('commission_base') === 'margin'
                        ? 'La comisión se calcula sobre el margen de la empresa, no el total de venta.'
                        : 'La comisión se calcula sobre el monto neto de la línea.'}
                    </p>
                  </div>
                  {watch('commission_base') === 'margin' && (
                    <TextField
                      dense
                      label="Tasa de margen (%)"
                      placeholder="0"
                      inputProps={{ ...register('margin_rate', { valueAsNumber: true }), type: 'number', step: '0.01', min: '0', max: '100' }}
                      hint="Porcentaje del neto que representa el margen de la empresa sobre este producto."
                    />
                  )}
                  <TextField
                    dense
                    label="Tasa de comisión (%)"
                    placeholder="0"
                    inputProps={{ ...register('commission_rate', { valueAsNumber: true }), type: 'number', step: '0.01', min: '0', max: '100' }}
                    hint="Es el respaldo: la pisan el trato del vendedor para este producto y, si no hay, su comisión por defecto."
                  />
                </>
              )}
            </div>
          </div>
        ) : commissionsEnabled !== false ? (
          initial?.id && parseFloat(initial.commission_rate ?? '0') > 0 && (
            <div>
              <p className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-3">Comisión vendedor</p>
              <div className="p-3 bg-glass-3 rounded-xl border border-divider">
                <span className="text-sm text-content-muted">
                  {initial.commission_rate}%
                  {initial.commission_base === 'margin' ? ' sobre margen' : ' sobre total'}
                  {' '}— solo administradores pueden modificar este campo.
                </span>
              </div>
            </div>
          )
        ) : null}

        {/* Inventario */}
        {inventoryEnabled !== false && <div>
          <p className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-3">Inventario</p>
          <div className="space-y-3 p-4 bg-glass-3 rounded-xl border border-divider">
            <label className="flex items-center gap-3 cursor-pointer">
              <input {...register('track_stock')} type="checkbox"
                className="w-4 h-4 rounded text-primary border-divider-strong focus:ring-primary" />
              <div>
                <p className="text-sm font-medium text-content">Controlar stock</p>
                <p className="text-xs text-content-subtle">Actualiza el stock automáticamente al emitir documentos y recepcionar mercadería</p>
              </div>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <TextField
                dense
                label="Punto de reorden"
                placeholder="0"
                inputProps={{ ...register('reorder_point'), type: 'number', step: '1', min: '0' }}
                hint="Alerta cuando el stock baja de esta cantidad"
              />
              <TextField
                dense
                label="Código de barras"
                placeholder="EAN-13, Code 128..."
                inputProps={register('barcode')}
              />
            </div>
          </div>
        </div>}

        {/* Catálogo: categoría + atributos dinámicos */}
        <div>
          <p className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-3">Catálogo</p>
          <div className="space-y-3 p-4 bg-glass-3 rounded-xl border border-divider">
            <SelectField
              dense
              label="Categoría"
              value={categoryId}
              onChange={e => { setCategoryId(e.target.value); setAttrValues({}) }}
              disabled={readOnly}
              hint="Habilita los atributos y la compatibilidad de catálogo."
            >
              <option value="">— Sin categoría —</option>
              {categories.map(c => (
              <option key={c.id} value={c.id}>{c.parent_name ? `${c.parent_name} › ${c.name}` : c.name}</option>
              ))}
            </SelectField>

            {categoryId && attributes.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                {attributes.map(attr => {
                  const val = attrValues[attr.code] ?? ''
                  const setVal = (v: string) => setAttrValues(prev => ({ ...prev, [attr.code]: v }))
                  return (
                    <div key={attr.id} className={attr.data_type === 'boolean' ? 'sm:col-span-2' : ''}>
                      {attr.data_type === 'boolean' ? (
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={val === 'true'}
                            onChange={e => setVal(e.target.checked ? 'true' : 'false')}
                            disabled={readOnly}
                            className="w-4 h-4 rounded text-primary border-divider-strong focus:ring-primary"
                          />
                          <span className="text-sm text-content">{attr.name}</span>
                        </label>
                      ) : (
                        <>
                          <label htmlFor={`producto-attr-${attr.code}`} className={labelCls}>
                            {attr.name}{attr.is_required && <span className="text-danger-solid"> *</span>}
                          </label>
                          {attr.data_type === 'select' ? (
                            <select id={`producto-attr-${attr.code}`} value={val} onChange={e => setVal(e.target.value)} className={inputCls} disabled={readOnly}>
                              <option value="">— Seleccionar —</option>
                              {attr.options.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          ) : (
                            <input id={`producto-attr-${attr.code}`}
                              value={val}
                              onChange={e => setVal(e.target.value)}
                              inputMode={attr.data_type === 'number' ? 'decimal' : undefined}
                              placeholder={attr.data_type === 'number' ? '0' : ''}
                              className={inputCls}
                              disabled={readOnly}
                            />
                          )}
                        </>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            {initial?.id && categoryId && (
              <ProductCatalogTab productId={initial.id} readOnly={readOnly} />
            )}
          </div>
        </div>

        {/* Contabilidad */}
        <div>
          <p className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-3">Contabilidad</p>
          <div className="space-y-3 p-4 bg-glass-3 rounded-xl border border-divider">
            <div>
              <label htmlFor="productspage-cuenta-contable-de-ventas" className={labelCls}>Cuenta contable de ventas</label>
              <Autocomplete id="productspage-cuenta-contable-de-ventas"
                items={accountOptions}
                value={salesAccountId}
                onChange={() => {}}
                onSelect={item => setSalesAccountId(item.id)}
                placeholder="Buscar cuenta de ingresos..."
                freeText={false}
                className={inputCls}
              />
              <p className="mt-1 text-xs text-content-subtle">Anula la cuenta de ingresos por defecto en comprobantes de venta.</p>
            </div>
            <div>
              <label htmlFor="productspage-cuenta-contable-de-compras" className={labelCls}>Cuenta contable de compras</label>
              <Autocomplete id="productspage-cuenta-contable-de-compras"
                items={accountOptions}
                value={purchaseAccountId}
                onChange={() => {}}
                onSelect={item => setPurchaseAccountId(item.id)}
                placeholder="Buscar cuenta de gastos..."
                freeText={false}
                className={inputCls}
              />
              <p className="mt-1 text-xs text-content-subtle">Anula la cuenta de gastos por defecto en facturas de compra.</p>
            </div>
            <div>
              <label htmlFor="productspage-centro-de-costo-por-defecto" className={labelCls}>Centro de costo por defecto</label>
              <Autocomplete id="productspage-centro-de-costo-por-defecto"
                items={costCenterOptions}
                value={defaultCostCenterId}
                onChange={() => {}}
                onSelect={item => setDefaultCostCenterId(item.id)}
                placeholder="Buscar centro de costo..."
                freeText={false}
                className={inputCls}
              />
              <p className="mt-1 text-xs text-content-subtle">
                Se imputa a este centro cuando la línea del documento no trae uno propio.
                Déjalo vacío en servicios genéricos: así hereda el del cliente, que es lo
                que sirve cuando el área que ejecuta depende del contrato.
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2 border-t border-divider">
          {readOnly ? (
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
              Cerrar
            </button>
          ) : (
            <>
              <button type="button" onClick={onClose}
                className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
                Cancelar
              </button>
              <button type="submit" disabled={isPending}
                className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover disabled:opacity-60 transition-colors">
                {isPending && <Loader2 size={14} className="animate-spin" />}
                {initial?.id ? 'Guardar cambios' : 'Crear ítem'}
              </button>
            </>
          )}
        </div>
        </fieldset>
      </form>
    </Modal>
  )
}

// ─── CommissionHistoryModal ───────────────────────────────────────────────────

const ACTION_LABELS: Record<number, string> = { 0: 'Creación', 1: 'Actualización', 2: 'Eliminación' }
/** Una entrada del historial: el campo y su par (valor anterior, nuevo). */
type CambioDeComision = [string, [string | null, string | null]]

const FIELD_LABELS: Record<string, string> = {
  is_commissionable: 'Afecto a comisión',
  detraction_code: 'Código de detracción (Perú)',
  commission_rate: 'Tasa comisión (%)',
  commission_base: 'Base comisión',
  margin_rate: 'Tasa margen (%)',
}
const BASE_LABELS: Record<string, string> = { total: 'Sobre total', margin: 'Sobre margen' }

function CommissionHistoryModal({ productId, onClose }: { productId: string; onClose: () => void }) {
  const { data, isLoading } = useProductCommissionHistory(productId)

  return (
    <Modal open onClose={onClose} title="Historial de comisiones" subtitle={data?.product_name ? data.product_name : undefined} size="2xl" dismissOnScrimClick={false}>
      <div className="overflow-y-auto flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={20} className="animate-spin text-content-subtle" />
          </div>
        ) : !data?.results.length ? (
          <p className="text-center text-sm text-content-subtle py-8">Sin cambios registrados en campos de comisión.</p>
        ) : (
          <div className="space-y-3">
            {data.results.map(entry => (
              <div key={entry.id} className="border border-divider rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-content-muted">
                    {ACTION_LABELS[entry.action] || 'Cambio'}
                  </span>
                  <div className="text-right">
                    <p className="text-xs text-content-muted">{entry.actor_name || entry.actor_email || 'Sistema'}</p>
                    <p className="text-xs text-content-subtle">
                      {new Date(entry.timestamp).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })}
                    </p>
                  </div>
                </div>
                <DataTable<CambioDeComision>
                  columns={[
                    { key: 'campo', header: 'Campo',
                      render: (fila: CambioDeComision) => (
                        <span className="font-medium text-content-muted">{FIELD_LABELS[fila[0]] || fila[0]}</span>
                      ) },
                    { key: 'anterior', header: 'Anterior',
                      render: (fila: CambioDeComision) => (
                        <span className="text-danger-content-soft">
                          {fila[0] === 'commission_base' ? (BASE_LABELS[fila[1][0] ?? ''] ?? fila[1][0]) : fila[1][0] ?? '—'}
                        </span>
                      ) },
                    { key: 'nuevo', header: 'Nuevo',
                      render: (fila: CambioDeComision) => (
                        <span className="text-success-content-soft">
                          {fila[0] === 'commission_base' ? (BASE_LABELS[fila[1][1] ?? ''] ?? fila[1][1]) : fila[1][1] ?? '—'}
                        </span>
                      ) },
                  ]}
                  rows={Object.entries(entry.changes) as CambioDeComision[]}
                  rowKey={fila => fila[0]}
                  minWidth={360}
                  surface={false}
                  caption="Campos que cambiaron en esta edición"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  )
}

// ─── ProductRow ───────────────────────────────────────────────────────────────

/**
 * Las tres senales que un vendedor de mostrador necesita ver SIN abrir la ficha:
 * de que origen es la pieza, a que vehiculo aplica y con que numero de fabricante
 * equivale. En repuestos la venta se decide por ahi: el cliente pregunta "una
 * correa para una Ranger 2.3" o llega con el numero de parte de Ford en la mano.
 *
 * Sale de `Product.attributes`, asi que un tenant que no use esos atributos no ve
 * nada: no es una fila de repuestos horneada en el catalogo generico.
 */
function ContextoRepuesto({ attributes }: { attributes?: Record<string, unknown> }) {
  if (!attributes) return null
  const texto = (v: unknown) => (typeof v === 'string' ? v.trim() : '')
  const lista = (v: unknown) =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && !!x.trim()) : []

  const origen = [texto(attributes.codigo_original), texto(attributes.procedencia)]
    .filter(Boolean).join(' · ')
  const aplica = lista(attributes.aplicaciones)
  const equiv = lista(attributes.equivalencias)
  if (!origen && !aplica.length && !equiv.length) return null

  const resumir = (xs: string[], n: number) =>
    xs.slice(0, n).join(' · ') + (xs.length > n ? ` +${xs.length - n}` : '')

  return (
    <div className="mt-0.5 space-y-0.5">
      {origen && (
        <p className="text-[11px] text-content-subtle truncate" title={origen}>{origen}</p>
      )}
      {aplica.length > 0 && (
        <p className="text-[11px] text-content-muted truncate" title={aplica.join(' · ')}>
          <span className="text-content-subtle">aplica:</span> {resumir(aplica, 2)}
        </p>
      )}
      {equiv.length > 0 && (
        <p className="text-[11px] text-content-muted truncate" title={equiv.join(' · ')}>
          <span className="text-content-subtle">equiv:</span> {resumir(equiv, 2)}
        </p>
      )}
    </div>
  )
}

/**
 * Las columnas del catálogo.
 *
 * 🔴 Esto era `ProductRow`, un componente que emitía su propio `<tr>` — el
 * bloqueo «componente-fila» del inventario. Era render puro, así que se desarma
 * en columnas sin perder nada.
 *
 * ⚠️ La moneda base y el impuesto llegan por PARÁMETRO: los resolvía con hooks
 * adentro, y una función que arma columnas no puede llamarlos. Se leen una vez
 * en la página.
 */
function columnasDeProducto({
  baseCurrency, tax, canEdit, onEdit, onView, onDeactivate, onRestore, onAddToOrder,
}: {
  baseCurrency: string
  tax: { name: string }
  canEdit: boolean
  onEdit: (p: ProductListItem) => void
  onView: (p: ProductListItem) => void
  onDeactivate: (p: ProductListItem) => void
  onRestore: (p: ProductListItem) => void
  onAddToOrder?: (p: ProductListItem) => void
}): DataTableColumn<ProductListItem>[] {
  return [
    { key: 'name', header: 'Ítem', sortable: true,
      render: p => (
        <div className="flex items-center gap-3 max-w-[24rem]">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0
            ${p.item_type === 'product' ? 'bg-primary-50' : 'bg-purple-50 dark:bg-purple-900/30'}`}>
            {p.item_type === 'product'
              ? <Package size={14} className="text-primary" />
              : <Wrench size={14} className="text-purple-500" />}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-medium text-content truncate" title={p.name}>{p.name}</p>
              {(p.sales_account || p.purchase_account) && (
                <span title="Cuenta contable configurada">
                  <BookOpen size={12} className="text-primary shrink-0" />
                </span>
              )}
            </div>
            <p className="text-xs text-content-subtle font-mono">{p.sku}</p>
            <ContextoRepuesto attributes={p.attributes} />
          </div>
        </div>
      ) },
    { key: 'item_type', header: 'Tipo', sortable: true,
      render: p => <TypeBadge type={p.item_type} /> },
    { key: 'unit_display', header: 'Unidad', sortable: true,
      render: p => <span className="text-content-muted">{p.unit_display}</span> },
    { key: 'available_stock', header: 'Stock', numeric: true,
      // Un producto que no controla stock NO es «cero»: es la ausencia del dato,
      // así que ordena al final en vez de mezclarse con los agotados.
      sortValue: p => (p.track_stock && p.available_stock != null ? Number(p.available_stock) : null),
      render: p => (p.track_stock && p.available_stock != null ? (
        <span className={`text-sm font-medium tabular-nums ${parseFloat(p.available_stock) <= 0 ? 'text-danger-content-soft' : 'text-content'}`}>
          {formatNumberForTenant(parseFloat(p.available_stock), 2)}
          <span className="ml-1 text-xs text-content-subtle font-normal">{p.unit}</span>
        </span>
      ) : (
        <span className="text-sm text-content-subtle" title="No controla stock">—</span>
      )) },
    { key: 'unit_price', header: 'Precio neto', numeric: true, sortable: true,
      sortValue: p => Number(p.unit_price),
      render: p => (
        <div>
          <p className="text-sm font-medium text-content">
            {p.price_currency && p.price_currency !== baseCurrency
              ? <span className="text-xs font-semibold text-primary mr-1">{p.price_currency}</span>
              : '$'}
            {/* `es-CL` con 0 decimales fijos le comía los céntimos a un precio en
                soles: los decimales salen de la moneda de la línea. */}
            {formatNumberForTenant(parseFloat(p.unit_price), currencyDecimals(p.price_currency))}
          </p>
          {p.is_tax_exempt && (
            <p className="text-xs text-warning-content-soft font-medium">Exento de {tax.name}</p>
          )}
        </div>
      ) },
    { key: 'commission_rate', header: 'Comisión', numeric: true, sortable: true,
      sortValue: p => Number(p.commission_rate ?? 0),
      render: p => (p.commission_rate && parseFloat(p.commission_rate) > 0 ? (
        <span className="text-sm font-medium text-primary">
          {p.commission_rate}%
          {p.commission_base === 'margin' && (
            <span className="ml-1 text-xs text-content-subtle font-normal">(margen)</span>
          )}
        </span>
      ) : <span className="text-sm text-content-subtle">—</span>) },
    { key: 'is_active', header: 'Estado', sortable: true,
      sortValue: p => (p.is_active ? 1 : 0),
      render: p => <StatusBadge active={p.is_active} /> },
    { key: 'acciones', header: '', width: 250,
      render: p => (
        <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
          {onAddToOrder && p.is_active && (
            <button
              onClick={() => onAddToOrder(p)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-primary border border-primary/30 rounded-lg hover:bg-primary-50 transition-colors"
              title="Agregar al pedido abierto"
            >
              <ShoppingCart size={11} />Agregar
            </button>
          )}
          {canEdit ? (
            <>
              <button
                onClick={() => onEdit(p)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors"
                title="Editar"
              >
                <PencilLine size={11} />Editar
              </button>
              <KebabMenu items={[
                { label: p.is_active ? 'Desactivar' : 'Reactivar', icon: p.is_active ? <PowerOff size={14} /> : <RefreshCw size={14} />, onClick: () => (p.is_active ? onDeactivate(p) : onRestore(p)), className: p.is_active ? 'text-danger-content-soft hover:bg-danger-surface' : 'text-success-content-soft hover:bg-success-surface' },
              ]} />
            </>
          ) : (
            <button
              onClick={() => onView(p)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors"
              title="Ver detalles"
            >
              <Eye size={11} />Ver
            </button>
          )}
        </div>
      ) },
  ]
}

// ─── ProductsPage ─────────────────────────────────────────────────────────────

export function ProductsPage() {
  const locale = useTenantLocale()
  // Se leen acá y viajan a las columnas: una función que arma columnas no
  // puede llamar hooks.
  const baseCurrency = useTenantCurrency()
  const tax = useTenantTax()
  const [page, setPage] = useState(1)
  // El orden va al SERVIDOR (`?ordering=`): esta lista la pagina el backend,
  // y ordenar en el navegador ordenaría solo la página visible.
  const [orden, setOrden] = useState<SortState | null>(null)
  const { data: driverInfoGlobal } = useDriverInfo()

  const filterConfig: FilterConfig = useMemo(() => [
    { key: 'search', type: 'search' as const, placement: 'quick' as const,
      placeholder: 'Nombre, SKU...' },
    { key: 'type', type: 'segment' as const, placement: 'quick' as const,
      options: [
        { value: '', label: 'Todos' },
        { value: 'product', label: 'Productos' },
        { value: 'service', label: 'Servicios' },
      ] },
    { key: 'active', type: 'segment' as const, placement: 'quick' as const, color: 'slate' as const,
      options: [
        { value: 'true', label: 'Activos' },
        { value: 'all', label: 'Todos' },
      ] },
  ], [])

  const filters = useFilters(filterConfig, { onFilterChange: () => setPage(1) })
  // Catálogo: filtro por categoría + atributos filtrables
  const [categoryFilter, setCategoryFilter] = useState('')
  const [attrFilters, setAttrFilters] = useState<Record<string, string>>({})
  const { data: catFilterData } = useProductCategories({ active: 'true' })
  const filterCategories = catFilterData?.categories ?? []
  const { data: filterAttrData } = useProductAttributes(
    categoryFilter ? { category: categoryFilter, filterable: 'true', active: 'true' } : undefined,
  )
  const filterAttributes = (categoryFilter ? filterAttrData?.attributes : undefined) ?? []
  const attrParam = useMemo(
    () => Object.entries(attrFilters)
      .filter(([, v]) => v !== '' && v != null)
      .map(([code, v]) => `${code}:${v}`),
    [attrFilters],
  )
  const [modalOpen, setModalOpen] = useState(false)
  const [modalReadOnly, setModalReadOnly] = useState(false)
  const [editing, setEditing] = useState<Partial<ProductListItem> | null>(null)
  const [confirming, setConfirming] = useState<{ product: ProductListItem; action: 'deactivate' | 'restore' } | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  const [serverErrors, setServerErrors] = useState<Record<string, string>>()
  const [historyProductId, setHistoryProductId] = useState<string | null>(null)
  const toast = useToast()

  const { activeTenant, user } = useAuthStore()
  const isAdmin = !!(user?.is_platform_admin || activeTenant?.roles?.includes('admin'))
  const { isModuleEnabled, can } = usePermission()
  const canCreateOrder = can('sales.orders', 'create')
  const addToCart = useCartStore(s => s.addItem)
  const canCreate = can('sales.products', 'create')
  const canEdit = can('sales.products', 'edit')

  const { data, isLoading } = useProducts({ ordering: sortStateToParam(orden),
    search: filters.params.search,
    active: (filters.params.active as 'true' | 'all') || 'true',
    type: filters.params.type as '' | 'product' | 'service' | undefined,
    category: categoryFilter || undefined,
    attr: attrParam.length ? attrParam : undefined,
    page,
    page_size: 20,
  })
  const { mutate: create, isPending: isCreating } = useCreateProduct()
  const { mutate: deactivate, isPending: isDeactivating } = useDeactivateProduct()
  const { mutate: restore, isPending: isRestoring } = useRestoreProduct()
  const updateMutation = useUpdateProduct(editing?.id || '')
  const importMutation = useImportProductsCSV()

  const handleModalSubmit = (data: any) => {
    setServerErrors(undefined)
    if (editing?.id) {
      updateMutation.mutate(data, {
        onSuccess: () => { setModalOpen(false); toast.success('Producto actualizado correctamente.') },
        onError: (e) => {
          const fields = extractFieldErrors(e)
          if (fields) { setServerErrors(fields) } else { toast.error('Error al actualizar el producto.') }
        },
      })
    } else {
      create(data, {
        onSuccess: () => { setModalOpen(false); toast.success('Producto creado correctamente.') },
        onError: (e) => {
          const fields = extractFieldErrors(e)
          if (fields) { setServerErrors(fields) } else { toast.error('Error al crear el producto.') }
        },
      })
    }
  }

  const handleConfirmAction = () => {
    if (!confirming) return
    if (confirming.action === 'deactivate') {
      deactivate(confirming.product.id, { onSuccess: () => setConfirming(null) })
    } else {
      restore(confirming.product.id, { onSuccess: () => setConfirming(null) })
    }
  }

  const products = data?.products || []
  const total = data?.count || 0
  const pages = data?.pages || 1

  return (
    <div data-tour="products-page" className="p-4 sm:p-6 max-w-7xl mx-auto">
      <PageHeader
        title="Productos y Servicios"
        subtitle={`${total} ${total === 1 ? 'ítem' : 'ítems'} en el catálogo`}
        help={HELP['sales.products']}
      >
        {canCreate && (
          <button data-tour="products-import" onClick={() => setImportOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-content border border-divider rounded-lg hover:bg-soft transition-colors">
            <Upload size={16} />Importar CSV
          </button>
        )}
        {canCreate && (
          <button data-tour="products-new" onClick={() => { setEditing(null); setServerErrors(undefined); setModalReadOnly(false); setModalOpen(true) }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors">
            <Plus size={16} />Nuevo ítem
          </button>
        )}
      </PageHeader>

      <FilterBar config={filterConfig} filters={filters} />

      {/* Catálogo: filtro por categoría + atributos filtrables */}
      {filterCategories.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5">
            <Tag size={14} className="text-content-subtle" />
            <select aria-label="Todas las categorías"
              value={categoryFilter}
              onChange={e => { setCategoryFilter(e.target.value); setAttrFilters({}); setPage(1) }}
              className="px-3 py-1.5 text-sm border border-divider rounded-lg bg-surface text-content focus:border-primary focus:ring-1 focus:ring-primary/50"
            >
              <option value="">Todas las categorías</option>
              {filterCategories.map(c => (
                <option key={c.id} value={c.id}>{c.parent_name ? `${c.parent_name} › ${c.name}` : c.name}</option>
              ))}
            </select>
          </div>
          {filterAttributes.map(attr => {
            const val = attrFilters[attr.code] ?? ''
            const setVal = (v: string) => { setAttrFilters(prev => ({ ...prev, [attr.code]: v })); setPage(1) }
            if (attr.data_type === 'select') {
              return (
                <select aria-label={attr.name} key={attr.id} value={val} onChange={e => setVal(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-divider rounded-lg bg-surface text-content focus:border-primary focus:ring-1 focus:ring-primary/50">
                  <option value="">{attr.name}: todos</option>
                  {attr.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              )
            }
            if (attr.data_type === 'boolean') {
              return (
                <select aria-label={attr.name} key={attr.id} value={val} onChange={e => setVal(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-divider rounded-lg bg-surface text-content focus:border-primary focus:ring-1 focus:ring-primary/50">
                  <option value="">{attr.name}: todos</option>
                  <option value="true">Sí</option>
                  <option value="false">No</option>
                </select>
              )
            }
            return (
              <input aria-label={attr.name} key={attr.id} value={val} onChange={e => setVal(e.target.value)}
                placeholder={attr.name}
                className="px-3 py-1.5 text-sm border border-divider rounded-lg bg-surface text-content placeholder-content-muted focus:border-primary focus:ring-1 focus:ring-primary/50 w-36" />
            )
          })}
          {(categoryFilter || attrParam.length > 0) && (
            <button onClick={() => { setCategoryFilter(''); setAttrFilters({}); setPage(1) }}
              className="text-xs text-content-muted hover:text-content underline">
              Limpiar
            </button>
          )}
        </div>
      )}

      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-x-auto shadow-card">
        {isLoading ? (
          <TableSkeleton rows={8} cols={7} />
        ) : products.length === 0 ? (
          <EmptyState
            icon={ShoppingBag}
            title="No hay productos"
            hint={filters.params.search ? 'Prueba con otro término.' : 'Crea el primer producto con el botón de arriba.'}
          />
        ) : (
          <DataTable<ProductListItem>
            {...ordenControlado(orden, s => { setOrden(s); setPage(1) })}
            columns={columnasDeProducto({
              baseCurrency, tax, canEdit,
              onEdit: p => { setEditing(p); setModalReadOnly(false); setServerErrors(undefined); setModalOpen(true) },
              onView: p => { setEditing(p); setModalReadOnly(true); setServerErrors(undefined); setModalOpen(true) },
              onDeactivate: p => setConfirming({ product: p, action: 'deactivate' }),
              onRestore: p => setConfirming({ product: p, action: 'restore' }),
              onAddToOrder: canCreateOrder
                ? (p => { addToCart(p); toast.success(`${p.name} agregado al pedido`) })
                : undefined,
            })}
            rows={products}
            rowKey={p => p.id}
            minWidth={1180}
            surface={false}
            data-tour="products-table"
            onRowClick={p => {
              setEditing(p); setModalReadOnly(!canEdit); setServerErrors(undefined); setModalOpen(true)
            }}
            caption="Productos y servicios del catálogo"
          />
        )}
      </div>

      {pages > 1 && (
        <Pagination
          page={page}
          pageSize={20}
          total={total}
          onPageChange={setPage}
          locale={locale}
          className="mt-4"
        />
      )}

      <ProductModal
        key={editing?.id ?? 'new'}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleModalSubmit}
        isPending={editing?.id ? updateMutation.isPending : isCreating}
        initial={editing as any}
        serverErrors={serverErrors}
        isAdmin={isAdmin}
        onShowHistory={editing?.id ? () => { setHistoryProductId(editing.id!); setModalOpen(false) } : undefined}
        inventoryEnabled={isModuleEnabled('inventory')}
        commissionsEnabled={isModuleEnabled('commissions')}
        readOnly={modalReadOnly}
      />

      {historyProductId && (
        <CommissionHistoryModal
          productId={historyProductId}
          onClose={() => { setHistoryProductId(null); setModalOpen(true) }}
        />
      )}

      <ConfirmModal
        open={confirming !== null}
        message={
          confirming?.action === 'deactivate'
            ? `¿Desactivar "${confirming.product.name}"? No podrá seleccionarlo para nuevos documentos.`
            : `¿Reactivar "${confirming?.product.name}"?`
        }
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirming(null)}
        isPending={isDeactivating || isRestoring}
        variant={confirming?.action === 'deactivate' ? 'danger' : 'success'}
      />

      <ImportCSVMapperModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Importar Productos"
        description="Suba un archivo CSV con los datos de sus productos/servicios. Si un SKU ya existe, se actualizará."
        systemFields={[
          ...PRODUCT_FIELDS_BASE,
          ...(driverInfoGlobal?.tax_fields
            ?.filter(f => f.type === 'boolean' && f.key !== 'is_tax_exempt')
            .map(f => ({
              key: f.key,
              label: `${f.label} (${f.rate}%) — sí/no`,
              aliases: [f.key],
            })) ?? []),
        ]}
        templateFileName="plantilla_productos.csv"
        onImport={(file, mapping) => importMutation.mutateAsync({ file, mapping })}
        isPending={importMutation.isPending}
      />

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  )
}
